import React, { useState, useEffect } from 'react';
import { Button, Form, Input, TimePicker } from 'components/shared/FormComponent';
import { Empty, Switch, Table, TableProps, Tooltip } from 'components/shared/AntComponent';
import { TableRowSelection } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

interface ChildProps {
    slots: any;
    applicantOptions: any;
    department: any;
    callbackFunc: (val: any) => void;
    duration: any;
    rowData: any;
    isPastDate: boolean;
}

interface DataType {
    key: number;
    regno: string;
    fname: number;
    lname: string;
    gender: string;
    // qualification: any;
    mobile_number: string;
    email: string;
    status: any;
}

const AddSlotScreen: React.FC<ChildProps> = ({ slots, rowData, applicantOptions, duration, department, callbackFunc, isPastDate }) => {
    const [form1] = Form.useForm();
    const { loading18 } = useSelector((state: any) => state.candidate);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [data, setData] = useState([]);
    const [totalPage, setTotalPage] = useState(0);
    const customLocale = {
        emptyText: <Empty className="p-2" description="No Applicants Available" />,
    };
    const deptOptions = department ? department?.map((item: any) => {
        return {
            value: item,
            text: item
        }
    }) : [];

    useEffect(() => {
        if (!applicantOptions) return;

        if (slots.length === 0) {
            // Case 1
            setData(applicantOptions);
            return;
        }

        if (rowData) {
            form1.setFieldsValue({
                slotStartTime: rowData?.slotStartTime,
                slotEndTime: rowData?.slotEndTime,
                slotName: rowData?.location,
                isActive: rowData?.isActive
            })
            // Case 2
            const currentSlotCandidates = rowData.associateCandidates?.split(',') || [];

            // Collect all candidates associated with other slots (excluding current one)
            const otherAssociated = slots
                .filter((s: any) => s.id !== rowData.id)
                .flatMap((s: any) => s.associateCandidates?.split(',') || []);

            // Final data = candidates in current slot + candidates not in any slot
            const filterVal = applicantOptions.filter((item: any) =>
                currentSlotCandidates.includes(item.id.toString()) ||
                !otherAssociated.includes(item.id.toString())
            );

            setData(filterVal);

            // Preselect rows for current slot
            setSelectedRowKeys(currentSlotCandidates.map((id: string) => Number(id)));
            return;
        }

        // Case 3 (new slot being created, rowData is null, but slots exist)
        const allAssociatedCandidates = slots.flatMap((slot: any) =>
            slot.associateCandidates?.split(',') || []
        );
        const filterVal = applicantOptions.filter(
            (item: any) => !allAssociatedCandidates.includes(item.id.toString())
        );
        setData(filterVal);
        if (!rowData) {
            form1.setFieldValue('isActive', true);
        }


    }, [applicantOptions, rowData, slots]);

    useEffect(() => {
        const start = form1.getFieldValue('slotStartTime');
        if (start && duration) {
            const end = dayjs(start).add(duration, 'minute');
            form1.setFieldValue('slotEndTime', end);
        }
    }, [duration, form1]);
    const handleTimeChange = async (value: any) => {
        if (!value) return;
        const durations = duration || 30; // fallback 30 mins if not set

        // Use the selected dayjs value directly
        const start = value?.second(0);
        const end = start.add(durations, 'minute');
        form1.setFieldValue('slotEndTime', end);
        form1.setFieldValue('slotStartTime', start);
    }

    const columns: TableProps<DataType>['columns'] = [
        {
            title: 'Reg No',
            dataIndex: 'regno',
            key: 'regno',
        },
        {
            title: 'First Name',
            dataIndex: 'fname',
            key: 'fname',
        },
        {
            title: 'Last Name',
            dataIndex: 'lname',
            key: 'lname',
        },
        {
            title: 'Specialisation',
            dataIndex: 'UG_course',
            key: 'UG_course',
            align: 'center',
            showSorterTooltip: { target: 'full-header' },
            filters: deptOptions,
            onFilter: (value: any, record: any) => record.UG_course?.indexOf(value as string) === 0,
            sorter: (a: any, b: any) => a.UG_course.length - b.UG_course.length,
            sortDirections: ['descend'],
        },
        // {
        //     title: 'Mobile No',
        //     dataIndex: 'mobile_number',
        //     key: 'mobile_number',
        //     align: 'center',
        // },
        {
            title: 'Email ',
            dataIndex: 'email',
            key: 'email',
        },
    ];
    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection: TableRowSelection<DataType> = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const hasSelected = selectedRowKeys.length > 0;
    // Pagination configuration
    const paginationConfig: TableProps<DataType>['pagination'] = {
        position: ['bottomRight'],
        current: pageIndex,
        pageSize: pageSize,
        total: totalPage,
        onChange: (page: number, pageSize: number) => {
            setPageIndex(page);
            setPageSize(pageSize);
            // getExamDetails(pageSize, page, searchValue);
        },
        itemRender: (current: any, type: string, originalElement: any) => {
            if (type === 'prev') {
                return <Button className="prev-btn">Previous</Button>;
            }
            if (type === 'next') {
                return <Button className="next-btn">Next</Button>;
            }
            return originalElement;
        },
    };
    const handleFilterChange = (value: any, filteredData: any) => {
        setPageIndex(value?.current);
        setPageSize(value?.pageSize);
        const dept = filteredData?.UG_course;
        const filterData = applicantOptions?.filter((item: any) => dept?.includes(item?.UG_course));
        setTotalPage(filterData?.length || data?.length);
    };

    const addForm = async () => {
        try {
            const value = await form1.validateFields();

            const slotInfo = {
                slotID: rowData ? rowData?.slotID : 0,
                slotStartTime: value.slotStartTime,
                slotEndTime: value.slotEndTime,
                associateCandidates: selectedRowKeys?.toString(),
                location: value.slotName || '',
                // account: value.account,
                // department: value.department,
                isActive: rowData ? value.isActive : true,
                disabled: false,
                id: rowData ? rowData?.id : slots?.length == 0 ? 1 : slots?.length + 1

            }
            callbackFunc(slotInfo);
            form1.resetFields();
            setSelectedRowKeys([]);
        } catch (errorInfo) {
            console.log("error", errorInfo);
        }
    }

    return (
        <div className='border-top pt-3'>
            <Form form={form1} onFinish={addForm} layout="vertical" >
                <div className='row mx-0 mb-2'>
                    <Form.Item label="Slot Name" name="slotName" className='col-md-6 ps-0'
                        rules={[{ required: true, message: 'This field is required' }]}>
                        <Input disabled={isPastDate} />
                    </Form.Item>
                    <Form.Item label="Start Time" name="slotStartTime" className='col-md-2 ps-1'
                        rules={[{ required: true, message: 'This field is required' }]}>
                        <TimePicker
                            onChange={(e) => handleTimeChange(e)}
                            format="hh:mm a"
                            className="w-100"
                            disabled={isPastDate}
                            disabledTime={() => {
                                if (!slots || slots.length === 0) return {}; // first slot has no restriction

                                // get the last slot’s start time
                                const prevStart = slots[slots.length - 1]?.slotStartTime;
                                if (!prevStart) return {};

                                const prevHour = dayjs(prevStart).hour();
                                const prevMinute = dayjs(prevStart).minute();
                                // Convert prevHour24 (24-hour) to 12-hour and keep AM/PM info
                                const isPM = prevHour >= 12;
                                let prevHour12 = prevHour % 12;
                                if (prevHour12 === 0) prevHour12 = 12;

                                return {
                                    disabledHours: () =>
                                        Array.from({ length: prevHour12 - 1 }, (_, i) => i + 1),
                                    // Array.from({ length: prevHour }, (_, i) => i), // disable all hours before prevHour
                                    disabledMinutes: (hour) => {
                                        if (hour === prevHour12) {
                                            return Array.from({ length: prevMinute + 1 }, (_, i) => i);
                                        }
                                        return [];
                                    },
                                };
                            }}
                        />
                    </Form.Item>
                    <Form.Item label="End Time" name="slotEndTime" className='col-md-2 ps-1'>
                        <TimePicker format="hh:mm a" className="w-100" disabled placeholder='Select start time' />
                    </Form.Item>
                    {rowData ?
                        <Form.Item
                            className="col-md-auto ps-2"
                            name="isActive"
                            valuePropName="checked"
                            label="Status"
                        >
                            <Switch disabled={isPastDate} />
                        </Form.Item> : ""}

                </div>
                <div className="col-md-12 custom-table pt-0">
                    <div className='d-flex'>
                        <h6>Associate Applicant </h6>
                        <div className='ms-auto col-auto bg-lightSecondary fw-bold rounded'> {hasSelected ? <>Selected<span className='text-primary fw-bold px-1'>{selectedRowKeys.length}</span>item(s)</> : null}</div>
                    </div>

                    <Table<DataType> scroll={{ x: 'calc(230px + 50%)' }} rowKey="id" rowSelection={rowSelection}
                        columns={columns} dataSource={data}
                        loading={loading18}
                        className='border table-responsive'
                        pagination={paginationConfig} locale={customLocale}
                        onChange={handleFilterChange} />
                </div>
                <div className="text-end">
                    <Tooltip title={selectedRowKeys?.length == 0 ? "Associate candidates to create slot" : ""}>
                        <Button type="primary" onClick={addForm} className='ms-auto mt-3 text-end' >Associate</Button>
                    </Tooltip>
                </div>
            </Form>
        </div>
    )
}

export default AddSlotScreen;